import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Plus, Loader2, X, MessageSquare, UserPlus, Lock, Users, Check } from "lucide-react";
import { messagingApi } from "@/api/messaging";
import { socialApi } from "@/api/social";
import ThreadView from "@/components/chat/ThreadView";

function ThreadRow({ t, onOpen, onClose }) {
  const [dx, setDx] = useState(0);
  const start = useRef(null);
  const onDown = (e) => { start.current = (e.touches?.[0]?.clientX ?? e.clientX); };
  const onMove = (e) => {
    if (start.current == null) return;
    const x = (e.touches?.[0]?.clientX ?? e.clientX);
    const d = Math.min(0, x - start.current);
    setDx(Math.max(d, -96));
  };
  const onUp = () => {
    if (dx < -60) { onClose(t.id); }
    setDx(0); start.current = null;
  };
  const groupName = t.title || (t.members || []).filter((m) => !m.self).map((m) => m.name).slice(0, 3).join(", ");
  const label = t.is_group ? (groupName || "Group chat") : t.other?.name;
  const initials = t.is_group ? null : (t.other?.name || "?").slice(0, 2).toUpperCase();
  return (
    <div className="relative overflow-hidden rounded-2xl" data-testid={`thread-row-${t.id}`}>
      <button onClick={() => onClose(t.id)} data-testid={`thread-close-${t.id}`}
        className="absolute right-0 top-0 bottom-0 w-24 bg-red-600 text-white flex flex-col items-center justify-center gap-1 font-mono text-[9px] font-black uppercase">
        <X size={16} /> Close
      </button>
      <button
        onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerLeave={() => start.current != null && onUp()}
        onClick={() => dx === 0 && onOpen(t)}
        data-testid={`thread-open-${t.id}`}
        style={{ transform: `translateX(${dx}px)`, transition: start.current == null ? "transform .18s ease" : "none" }}
        className="relative w-full bg-white border-2 border-charcoal rounded-2xl px-3 py-3 flex items-center gap-3 shadow-[3px_3px_0px_#1A1A1A] text-left">
        <span className="w-11 h-11 rounded-full bg-charcoal border-2 border-charcoal grid place-items-center text-gold font-mono text-xs font-black shrink-0">
          {t.is_group ? <Users size={17} /> : initials}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-mono text-[12px] font-bold text-charcoal truncate">
            {label}{t.is_group && <span className="text-charcoal/40 font-normal"> · {t.member_count}</span>}
          </p>
          <p className="font-mono text-[10px] text-charcoal/50 truncate">{t.last_message || "No messages yet"}</p>
        </div>
        {!t.is_group && !t.friends && <Lock size={13} className="text-red-500 shrink-0" />}
      </button>
    </div>
  );
}

// Friend picker. single=true → tap to start a 1:1. Otherwise multi-select for a group.
function FriendPicker({ onStart, onClose, allowGroups, title = "Start a Chat", exclude = [] }) {
  const [friends, setFriends] = useState(null);
  const [sel, setSel] = useState([]);
  const [gname, setGname] = useState("");
  useEffect(() => { socialApi.friends().then((r) => setFriends((r.friends || []).filter((f) => !exclude.includes(f.id)))).catch(() => setFriends([])); }, []);
  const toggle = (f) => setSel((s) => (s.includes(f.id) ? s.filter((x) => x !== f.id) : [...s, f.id]));
  return (
    <div className="absolute inset-0 z-40 bg-black/70 flex items-end" data-testid="friend-picker">
      <div className="w-full bg-cream border-t-4 border-charcoal rounded-t-3xl p-5 max-h-[82dvh] overflow-y-auto no-scrollbar">
        <div className="flex items-center gap-2 pb-3 border-b-2 border-charcoal/10 mb-3">
          <UserPlus size={16} className="text-gold" />
          <h3 className="font-serif text-xl text-charcoal flex-1">{title}</h3>
          <button onClick={onClose} data-testid="picker-close-btn" className="w-8 h-8 grid place-items-center rounded-lg border-2 border-charcoal bg-white shadow-[1.5px_1.5px_0px_#1A1A1A]"><X size={15} /></button>
        </div>
        {allowGroups && sel.length >= 2 && (
          <input value={gname} onChange={(e) => setGname(e.target.value)} data-testid="group-name-input" placeholder="Group name (optional)"
            className="w-full mb-3 bg-white border-2 border-charcoal rounded-full px-4 py-2.5 font-mono text-xs text-charcoal outline-none focus:ring-2 focus:ring-gold" />
        )}
        {friends == null ? (
          <div className="grid place-items-center py-8"><Loader2 size={20} className="text-charcoal/40 animate-spin" /></div>
        ) : friends.length === 0 ? (
          <p className="font-mono text-[11px] text-charcoal/50 py-6 text-center">No mutual friends here yet. Add friends in the directory to unlock chats.</p>
        ) : friends.map((f) => {
          const on = sel.includes(f.id);
          return (
            <button key={f.id} onClick={() => (allowGroups ? toggle(f) : onStart([f.id], ""))} data-testid={`picker-friend-${f.id}`}
              className={`w-full mb-2 border-2 border-charcoal rounded-2xl px-3 py-2.5 flex items-center gap-3 shadow-[2px_2px_0px_#1A1A1A] text-left active:translate-x-[2px] active:translate-y-[2px] active:shadow-none ${on ? "bg-gold" : "bg-white"}`}>
              <span className="w-9 h-9 rounded-full bg-charcoal grid place-items-center text-gold font-mono text-[10px] font-black shrink-0">{(f.name || "?").slice(0, 2).toUpperCase()}</span>
              <span className="font-mono text-[12px] font-bold text-charcoal truncate flex-1">{f.name}</span>
              {allowGroups && (on ? <Check size={16} className="text-charcoal" /> : <Plus size={16} className="text-charcoal/40" />)}
            </button>
          );
        })}
        {allowGroups && sel.length > 0 && (
          <button onClick={() => onStart(sel, gname)} data-testid="picker-start-btn"
            className="sticky bottom-0 w-full mt-1 bg-charcoal text-gold font-mono text-[11px] font-black uppercase tracking-widest py-3 rounded-full border-2 border-gold shadow-[3px_3px_0px_#D4AF37]">
            {sel.length === 1 ? "Start chat" : `Start group · ${sel.length}`}
          </button>
        )}
      </div>
    </div>
  );
}

export default function P2PInbox({ onBack, embedded, enableGroups = false }) {
  const [threads, setThreads] = useState(null);
  const [picker, setPicker] = useState(false);
  const [active, setActive] = useState(null); // {threadId, other, isGroup}

  useEffect(() => {
    const raw = typeof window !== "undefined" && sessionStorage.getItem("mist_pending_dm");
    if (raw) {
      sessionStorage.removeItem("mist_pending_dm");
      try {
        const { threadId, other } = JSON.parse(raw);
        if (threadId) setActive({ threadId, other });
      } catch { /* ignore malformed */ }
    }
  }, []);

  const load = () => messagingApi.threads().then((r) => setThreads(r.threads)).catch(() => setThreads([]));
  useEffect(() => {
    load();
    const id = setInterval(load, 4000);
    return () => clearInterval(id);
  }, []);

  const start = async (ids, gname) => {
    try {
      if (ids.length === 1 && !enableGroups) {
        const r = await messagingApi.openThread(ids[0]);
        setPicker(false);
        setActive({ threadId: r.thread_id, other: r.other });
        return;
      }
      const r = await messagingApi.createGroup(ids, gname);
      setPicker(false);
      setActive({ threadId: r.thread.id, other: r.thread.other, isGroup: r.thread.is_group });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start chat");
    }
  };

  const closeThread = async (tid) => {
    setThreads((ts) => (ts || []).filter((t) => t.id !== tid));
    try { await messagingApi.closeThread(tid); toast("Thread closed — reopens on new message", { icon: "📥" }); }
    catch { load(); }
  };

  if (active) return <ThreadView threadId={active.threadId} other={active.other} enableGroups={enableGroups} onBack={() => { setActive(null); load(); }} />;

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-cream" data-testid="p2p-inbox">
      {picker && <FriendPicker onStart={start} onClose={() => setPicker(false)} allowGroups={enableGroups} title={enableGroups ? "New chat" : "Start a Chat"} />}
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3">
        {!embedded && (
        <button onClick={onBack} data-testid="p2p-back-btn" aria-label="Back" className="w-10 h-10 rounded-full bg-white border-2 border-charcoal grid place-items-center shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
          <ArrowLeft size={18} strokeWidth={2.4} />
        </button>
        )}
        <div className="leading-none flex-1">
          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-gold">{enableGroups ? "Messages" : "P2P · My Chats"}</p>
          <h1 className="font-serif text-2xl text-charcoal">{enableGroups ? "Chats" : "Private Inbox"}</h1>
        </div>
      </header>
      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-24 space-y-2.5">
        {threads == null ? (
          <div className="grid place-items-center py-10"><Loader2 size={20} className="text-charcoal/40 animate-spin" /></div>
        ) : threads.length === 0 ? (
          <div className="text-center pt-12">
            <MessageSquare size={30} className="text-charcoal/30 mx-auto" />
            <p className="mt-3 font-mono text-[11px] text-charcoal/50">No open conversations.</p>
            <button onClick={() => setPicker(true)} data-testid="p2p-empty-new-btn" className="mt-4 inline-flex items-center gap-2 bg-charcoal text-gold font-mono text-[10px] font-black uppercase tracking-widest px-4 py-2.5 rounded-full border-2 border-gold shadow-[3px_3px_0px_#D4AF37]"><Plus size={14} /> {enableGroups ? "New chat" : "Start a chat"}</button>
          </div>
        ) : (
          <>
            <p className="font-mono text-[8px] uppercase tracking-widest text-charcoal/40 pl-1">Swipe a thread left to close it</p>
            {threads.map((t) => <ThreadRow key={t.id} t={t} onOpen={(th) => setActive({ threadId: th.id, other: th.other, isGroup: th.is_group })} onClose={closeThread} />)}
          </>
        )}
      </div>
      {/* Floating compose (FB-style) */}
      <button onClick={() => setPicker(true)} data-testid="p2p-new-btn" aria-label="New chat"
        className="absolute bottom-5 right-5 z-30 w-14 h-14 rounded-full bg-gold border-2 border-charcoal grid place-items-center shadow-[4px_4px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none">
        {enableGroups ? <UserPlus size={22} strokeWidth={2.4} className="text-charcoal" /> : <Plus size={24} strokeWidth={2.6} className="text-charcoal" />}
      </button>
    </div>
  );
}
